import React, { ReactNode, useCallback, useState } from 'react'
import styles from './popoverMenu.module.scss'
import { OverlayTrigger, Popover, OverlayTriggerProps } from 'react-bootstrap'
import { ReactComponent as ChevronIcon } from 'images/icons/icn_arrow_down.svg'
import classNames from 'classnames'
import useFocusTrap from 'hooks/use-focus-trap'
import useElement from 'hooks/use-element'

interface Props {
  triggerContent: ReactNode
  triggerEvent?: OverlayTriggerProps['trigger']
  triggerAriaLabel?: string
  menuId: string
  withChevron?: boolean
  children: ReactNode
  classes?: {
    popover?: string
    trigger?: string
    triggerActive?: string
    triggerDisabled?: string
  }
  isDisabled?: boolean
  controls?: { isOpen: boolean; setIsOpen: (open: boolean) => void }
}

const PopoverMenu = ({
  children,
  menuId,
  triggerContent,
  triggerAriaLabel,
  triggerEvent = 'click',
  withChevron = false,
  classes = {},
  isDisabled = false,
  controls,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const { isOpen: isOpenControlled, setIsOpen: setIsOpenControlled } = controls ?? {}
  const isMenuOpen = isOpenControlled ?? isOpen
  const handleOpen = useCallback(
    () => (setIsOpenControlled ? setIsOpenControlled(true) : setIsOpen(true)),
    [setIsOpenControlled]
  )
  const handleClose = useCallback(
    () => (setIsOpenControlled ? setIsOpenControlled(false) : setIsOpen(false)),
    [setIsOpenControlled]
  )
  const [triggerInternal, triggerRefInternal] = useElement<HTMLButtonElement>()

  const { trapContainerRef } = useFocusTrap<HTMLDivElement, HTMLButtonElement>({
    isActive: isMenuOpen,
    returnFocusElement: triggerInternal ?? undefined,
    onPressEscape: handleClose,
  })

  return (
    <OverlayTrigger
      trigger={triggerEvent}
      placement='bottom-end'
      overlay={
        <Popover
          id={menuId}
          bsPrefix={styles.popoverMenu}
          className={classes.popover}
          onClick={handleClose}
          role='popup'
          aria-expanded={true}
        >
          <div ref={trapContainerRef}>{children}</div>
        </Popover>
      }
      onEnter={handleOpen}
      onExit={handleClose}
      rootClose
    >
      <button
        ref={triggerRefInternal}
        className={classNames(styles.trigger, classes.trigger, {
          [styles.menuActive]: isMenuOpen,
          [classes.triggerActive || '']: isMenuOpen,
          [classes.triggerDisabled || '']: isDisabled,
        })}
        aria-haspopup='true'
        aria-expanded={isMenuOpen}
        aria-label={triggerAriaLabel}
        disabled={isDisabled}
      >
        {triggerContent}
        {withChevron && <ChevronIcon className={styles.chevron} aria-hidden='true' />}
      </button>
    </OverlayTrigger>
  )
}

export default PopoverMenu
